/**
 * Place an order from a quote: Review & accept (typed-name signature) → Pay
 * the advance from the organization wallet (top up by UPI/card when short) →
 * Order placed. The amount due comes from the quote (`advance_due`), never
 * from this page. Doc: docs/marketplace_get_it_built.md (plan D3, D9).
 */
import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { fmtDate, mktError, money } from '@/api/marketplace'
import { getQuote, placeOrder, type Order, type Quote } from '@/api/marketplaceBuild'
import { useAuth } from '@/auth/AuthContext'
import MarketplaceShell, { Check, Steps } from '../MarketplaceShell'
import HostCallout from './HostCallout'
import PaymentStages from './PaymentStages'
import WalletTopUp from './WalletTopUp'
import {
  briefItems, coveredCount, dateTime, ErrorPanel, Loading, monthlyShort, quoteLive, quoteStatus, weeksText,
} from './clientShared'

const LABELS = ['Review & accept', 'Pay the advance', 'Order placed']

export default function PlaceOrderPage() {
  const { quoteId = '' } = useParams()
  const { user } = useAuth()
  const [q, setQ] = useState<Quote | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [step, setStep] = useState(1)
  const [listing, setListing] = useState(false)
  const [agree, setAgree] = useState(false)
  const [sig, setSig] = useState('')
  const [placing, setPlacing] = useState(false)
  const [due, setDue] = useState<{ short: number } | null>(null)
  const [refused, setRefused] = useState<string | null>(null)
  const [payError, setPayError] = useState<string | null>(null)
  const [order, setOrder] = useState<Order | null>(null)
  const [signedAt] = useState(() => new Date())

  const load = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const r = await getQuote(Number(quoteId))
      setQ(r)
      setListing(!!r.request?.listing_allowed)
    } catch (e) {
      setError(mktError(e, 'This quote could not be loaded.').message)
    } finally {
      setLoading(false)
    }
  }, [quoteId])
  useEffect(() => { load() }, [load])

  const pay = async () => {
    if (!q) return
    setPlacing(true); setPayError(null); setRefused(null)
    try {
      const o = await placeOrder({ quote_id: q.id, signed_name: sig.trim(), listing_allowed: listing })
      setOrder(o); setDue(null); setStep(3)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (e) {
      const err = mktError(e, 'The order could not be placed.')
      if (err.code === 'BUILD_PAYMENT_DUE') {
        setDue({ short: Number(err.extra.short_minor) || 0 })
      } else if (err.code === 'BUILD_NOT_ALLOWED') {
        setRefused(err.message)
      } else {
        setPayError(err.message)
        toast.error(err.message)
      }
    } finally {
      setPlacing(false)
    }
  }

  if (loading) return <MarketplaceShell><Loading text="Loading the quote…" /></MarketplaceShell>
  if (error || !q) return <MarketplaceShell><ErrorPanel message={error || 'This quote could not be loaded.'} onRetry={load} /></MarketplaceShell>

  const cur = q.currency
  const req = q.request
  const items = briefItems(req)
  const adv = q.stages[0]
  const advDue = q.advance_due
  const rest = q.price_minor - (advDue?.base_minor ?? q.advance_minor)
  const org = req?.organization || user?.organization || 'your organization'
  const builder = q.builder.name
  const sigOk = sig.trim().length >= 3
  const approver = !!(q.approver ?? req?.approver)
  const blocked = !order && (q.role === 'builder' || !quoteLive(q) || !approver)
  const st = quoteStatus(q)
  const releaseWithAccept = q.terms?.advance_release === 'builder_accepts'

  if (blocked) {
    return (
      <MarketplaceShell>
        <div className="mkt-wrap" style={{ paddingTop: 32, paddingBottom: 64, display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 760 }}>
          <h1 style={{ fontSize: 28 }}>Place your order</h1>
          <div className="mkt-banner warn" role="alert">
            <div>
              <strong>This quote can’t be ordered</strong>
              {q.role === 'builder'
                ? 'This is your own estimate — only the client can order it.'
                : !approver
                  ? 'Only your organization’s admins or project owners can place an order, because it spends the organization’s money. Ask one of them to open this quote.'
                  : q.expired
                    ? `This quote has expired. Ask ${builder} for a new version from your request.`
                    : `This quote is “${st.label.toLowerCase()}”, so it can no longer be ordered.`}
            </div>
          </div>
          <div className="mkt-row">
            <Link className="btn btn-secondary" to={`/marketplace/build/quotes/${q.id}`}>Back to the quote</Link>
            {req && <Link className="btn btn-ghost" to={`/marketplace/build/requests/${req.id}`}>All estimates</Link>}
          </div>
        </div>
      </MarketplaceShell>
    )
  }

  const tracker: { mark: 'ok' | 'wait' | 'todo'; t: string; d: string; when: string }[] = order ? [
    { mark: 'ok', t: 'Order placed and signed', d: `${order.signed_name || sig} accepted the quote for ${order.client?.name || org}`, when: dateTime(order.signed_at || order.created_at) },
    { mark: 'ok', t: 'Advance received — held by Twasta', d: `${money(advDue?.base_minor ?? q.advance_minor, cur)}${advDue?.service_fee_minor ? ` + ${money(advDue.service_fee_minor, cur)} service fee` : ''}, from the organization wallet`, when: dateTime(order.created_at) },
    { mark: 'wait', t: `Waiting for ${builder} to accept`, d: order.accept_by ? `They have until ${dateTime(order.accept_by)}` : 'They are told straight away', when: order.accept_by ? `by ${fmtDate(order.accept_by)}` : '' },
    { mark: 'todo', t: `Project created in ${builder}’s account`, d: 'Your brief is attached as its specification; you get a working copy to try', when: '' },
    { mark: 'todo', t: q.stages[1] ? `First stage: ${q.stages[1].name}` : 'The work starts', d: 'You review it and approve it before Twasta pays for it', when: '' },
  ] : []
  const markStyle = (m: 'ok' | 'wait' | 'todo') => m === 'ok'
    ? { background: 'var(--color-success)', color: '#fff', border: 0 }
    : m === 'wait'
      ? { background: 'var(--color-warning-weak)', color: 'var(--color-warning)', border: 0 }
      : { background: 'var(--color-bg-secondary)', color: 'var(--color-text-secondary)', border: '2px solid var(--color-border-strong, var(--color-border))' }

  return (
    <MarketplaceShell>
      <div className="mkt-wrap" style={{ paddingTop: 24, paddingBottom: 64, display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div className="mkt-row" style={{ justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <div>
            <h1 style={{ fontSize: 30 }}>{step === 3 ? 'Order placed' : 'Place your order'}</h1>
            <p className="mkt-muted" style={{ margin: 0 }}>{req?.title || 'Your app'} · quote #{q.id} v{q.version} from {builder}</p>
          </div>
          <Steps labels={LABELS} current={step} done={step === 3} />
        </div>

        <div className="mkt-split">
          <div className="mkt-main" style={{ gap: 16 }}>
            {step === 1 && (
              <>
                <section className="mkt-panel" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div className="mkt-row" style={{ justifyContent: 'space-between' }}>
                    <h2>What you are ordering</h2>
                    <Link to={`/marketplace/build/quotes/${q.id}`} style={{ fontSize: 14 }}>Open the full quote</Link>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, background: 'var(--color-raised, var(--color-bg))', borderRadius: 12, padding: '14px 16px', fontSize: 14 }}>
                    <div><div className="mkt-faint">To build</div><strong style={{ fontSize: 18 }}>{money(q.price_minor, cur)}</strong></div>
                    <div><div className="mkt-faint">Live in</div><strong style={{ fontSize: 18 }}>{weeksText(q.weeks)}</strong></div>
                    {items.length > 0 && <div><div className="mkt-faint">Scope</div><strong style={{ fontSize: 18 }}>{coveredCount(q, items)} of {items.length} items</strong></div>}
                    <div><div className="mkt-faint">To use, from install</div><strong style={{ fontSize: 18 }}>{monthlyShort(q.monthly, cur)}</strong></div>
                  </div>
                  <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
                    {[
                      { k: 'Built', tone: 'info', t: `In ${builder}’s Twasta account`, d: 'A new project, just for this order.' },
                      { k: 'Reviewed', tone: 'info', t: 'By you, with sample data', d: 'Try the working copy at every stage.' },
                      { k: 'Installed', tone: 'ok', t: `In ${org}`, d: 'At sign-off: your own copy and database, in My apps.' },
                    ].map((d) => (
                      <li key={d.k} style={{ border: '1px solid var(--color-border)', borderRadius: 12, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <span className={`mkt-pill ${d.tone}`} style={{ alignSelf: 'flex-start' }}>{d.k}</span>
                        <strong style={{ fontSize: 14 }}>{d.t}</strong>
                        <span className="mkt-faint">{d.d}</span>
                      </li>
                    ))}
                  </ol>
                </section>

                <section className="mkt-panel" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <h2>Payment terms</h2>
                  <p className="mkt-faint" style={{ margin: 0 }}>Twasta’s standard terms for every order. Today you pay only the advance.</p>
                  <PaymentStages stages={q.stages} rows={q.stages} amountMinor={q.price_minor} currency={cur} who="client" advanceRelease={q.terms?.advance_release} />
                </section>

                <section className="mkt-panel" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <h2>Order options</h2>
                  <div className="mkt-field">
                    <span id="po-listing" style={{ fontWeight: 600, fontSize: 14 }}>May {builder} also sell this app to other companies?</span>
                    <div role="group" aria-labelledby="po-listing" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 10 }}>
                      <button type="button" className={`mkt-choice${!listing ? ' on' : ''}`} aria-pressed={!listing} onClick={() => setListing(false)}>
                        <strong>No — only for us</strong><span className="mkt-muted" style={{ fontSize: 14 }}>Nobody else gets this app.</span>
                      </button>
                      <button type="button" className={`mkt-choice${listing ? ' on' : ''}`} aria-pressed={listing} onClick={() => setListing(true)}>
                        <strong>Yes, without our data</strong><span className="mkt-muted" style={{ fontSize: 14 }}>{builder} may offer the app to others. Your data and settings never go with it.</span>
                      </button>
                    </div>
                  </div>
                </section>

                <section className="mkt-panel" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <h2>Accept the order</h2>
                  <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '12px 14px', border: '1px solid var(--color-border)', borderRadius: 12, cursor: 'pointer', fontSize: 15 }}>
                    <input type="checkbox" checked={agree} onChange={() => setAgree(!agree)} style={{ width: 18, height: 18, marginTop: 2, accentColor: 'var(--color-accent)' }} />
                    <span>I accept quote #{q.id} version {q.version} and Twasta’s standard build terms on behalf of <strong>{org}</strong>. We pay Twasta stage by stage; our copy of the app and all its data belong to us.</span>
                  </label>
                  <div className="mkt-field" style={{ maxWidth: 420 }}>
                    <label htmlFor="po-sig">Type your full name to sign</label>
                    <input id="po-sig" value={sig} maxLength={120} onChange={(e) => setSig(e.target.value)} placeholder={user?.user_name || 'Your full name'} autoComplete="name" style={{ fontFamily: 'var(--font-mono, monospace)' }} />
                    <span className="mkt-faint">Signed by {sig.trim() || '…'}{user?.email ? ` · ${user.email}` : ''} · {signedAt.toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
                  </div>
                </section>

                <div className="mkt-row" style={{ justifyContent: 'space-between' }}>
                  <Link className="btn btn-secondary" to={`/marketplace/build/quotes/${q.id}`}>Back to the quote</Link>
                  <button type="button" className="btn btn-primary" disabled={!agree || !sigOk} onClick={() => { setStep(2); window.scrollTo({ top: 0, behavior: 'smooth' }) }}>Accept and continue to payment</button>
                </div>
                {(!agree || !sigOk) && <span className="mkt-faint" style={{ textAlign: 'right' }}>Tick the box and type your full name to continue.</span>}
              </>
            )}

            {step === 2 && (
              <>
                <section className="mkt-panel" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <h2>Pay the advance</h2>
                  <div className="mkt-choice on" style={{ cursor: 'default' }}>
                    <span className="mkt-row" style={{ justifyContent: 'space-between', width: '100%' }}>
                      <strong>{org} wallet</strong>
                      <span className="mkt-pill ok">Paid from here</span>
                    </span>
                    <span className="mkt-muted" style={{ fontSize: 14 }}>The advance is taken from your organization wallet now; later stages are taken from it as the builder submits them. If the wallet is short, you can add money by UPI or card.</span>
                  </div>
                  <p className="mkt-faint" style={{ margin: 0 }}>GST was charged when money was put into the wallet, so there is no second GST line on this payment.</p>

                  {refused && (
                    <div className="mkt-banner bad" role="alert">
                      <div><strong>You can’t place this order</strong>{refused} Only your organization’s admins or project owners can spend its money — ask one of them to place the order.</div>
                    </div>
                  )}
                  {payError && !refused && !due && (
                    <div className="mkt-banner bad" role="alert"><div><strong>The order wasn’t placed</strong>{payError}</div></div>
                  )}
                  {due && (
                    <div className="mkt-banner warn" role="alert">
                      <div className="mkt-col" style={{ gap: 10 }}>
                        <strong>Not enough money in the wallet</strong>
                        <WalletTopUp currency={cur} suggestMinor={due.short} onPaid={pay} />
                        <span className="mkt-faint">Once the money is in, the order is placed straight away.</span>
                      </div>
                    </div>
                  )}
                </section>

                <div className="mkt-row" style={{ justifyContent: 'space-between' }}>
                  <button type="button" className="btn btn-secondary" onClick={() => setStep(1)} disabled={placing}>Back</button>
                  <button type="button" className="btn btn-primary" onClick={pay} disabled={placing || !!refused}>
                    {placing ? 'Placing the order…' : `Pay ${money(advDue?.total_minor ?? q.advance_minor, cur)} and place the order`}
                  </button>
                </div>
              </>
            )}

            {step === 3 && order && (
              <>
                <section className="mkt-panel" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <div className="mkt-row" style={{ gap: 14 }}>
                    <span aria-hidden="true" style={{ width: 52, height: 52, borderRadius: 999, background: 'var(--color-success-weak)', color: 'var(--color-success)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Check size={26} /></span>
                    <div style={{ flex: '1 1 auto' }}>
                      <h2 style={{ fontSize: 22 }}>Order {order.order_no} placed</h2>
                      <p className="mkt-muted" style={{ fontSize: 14, margin: 0 }}>
                        {money(advDue?.total_minor ?? q.advance_minor, cur)} paid from your wallet. Twasta holds the {money(advDue?.base_minor ?? q.advance_minor, cur)} advance;
                        {releaseWithAccept ? ` it goes to ${builder} only when they accept the order.` : ` nothing goes to ${builder} until you approve the first stage.`}
                      </p>
                    </div>
                  </div>
                  <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column' }} aria-label="Order progress">
                    {tracker.map((t) => (
                      <li key={t.t} style={{ display: 'flex', gap: 14, alignItems: 'flex-start', padding: '10px 0', borderBottom: '1px solid var(--color-border)' }}>
                        <span aria-hidden="true" style={{ width: 26, height: 26, borderRadius: 999, flex: '0 0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, ...markStyle(t.mark) }}>{t.mark === 'ok' ? '✓' : t.mark === 'wait' ? '…' : ''}</span>
                        <div style={{ flex: '1 1 auto' }}>
                          <strong style={{ fontSize: 15 }}><span className="mkt-sr">{t.mark === 'ok' ? 'Done: ' : t.mark === 'wait' ? 'In progress: ' : 'Next: '}</span>{t.t}</strong>
                          <div className="mkt-faint">{t.d}</div>
                        </div>
                        <span className="mkt-faint">{t.when}</span>
                      </li>
                    ))}
                  </ol>
                  {order.accept_by && (
                    <div className="mkt-banner plain" style={{ fontSize: 14 }}>
                      <div>{builder} has until <strong style={{ display: 'inline' }}>{dateTime(order.accept_by)}</strong> to accept. If they don’t, the advance comes back to your wallet in full.</div>
                    </div>
                  )}
                  <div className="mkt-row">
                    <Link className="btn btn-primary" to={`/marketplace/build/orders/${order.id}`}>Open the order</Link>
                    <Link className="btn btn-secondary" to="/marketplace/my-apps">See it in My apps</Link>
                  </div>
                </section>
              </>
            )}
          </div>

          <aside className="mkt-aside" style={{ maxWidth: 380 }}>
            <div className="mkt-panel" style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 6, fontSize: 15 }}>
              <strong style={{ fontSize: 17, marginBottom: 4 }}>{step === 3 ? 'Paid today' : 'Due today'}</strong>
              <div className="mkt-row" style={{ justifyContent: 'space-between', flexWrap: 'nowrap' }}>
                <span className="mkt-muted">{adv ? `${adv.name} — ${adv.pct}% of ${money(q.price_minor, cur)}` : 'Advance'}</span>
                <strong>{money(advDue?.base_minor ?? q.advance_minor, cur)}</strong>
              </div>
              {advDue && advDue.service_fee_minor > 0 && (
                <div className="mkt-row" style={{ justifyContent: 'space-between' }}><span className="mkt-muted">Twasta service fee {advDue.service_fee_percent}%</span><span>{money(advDue.service_fee_minor, cur)}</span></div>
              )}
              <div className="mkt-row" style={{ justifyContent: 'space-between', borderTop: '1px solid var(--color-border)', paddingTop: 10, marginTop: 4, fontSize: 18 }}>
                <strong>{step === 3 ? 'Paid' : 'Pay now'}</strong><strong>{money(advDue?.total_minor ?? q.advance_minor, cur)}</strong>
              </div>
              <p className="mkt-faint" style={{ margin: 0 }}>GST was charged when money was added to your wallet. The other {money(rest, cur)} is paid stage by stage, when {builder} submits each one for your approval.</p>
              <div className="mkt-row" style={{ justifyContent: 'space-between', borderTop: '1px dashed var(--color-border)', paddingTop: 10, flexWrap: 'nowrap' }}>
                <span className="mkt-muted">From install, to use it</span><strong style={{ textAlign: 'right' }}>{monthlyShort(q.monthly, cur)}</strong>
              </div>
              <div style={{ marginTop: 6 }}><HostCallout currency={cur} /></div>
            </div>
            <div className="mkt-panel" style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <strong>You are protected</strong>
              {[
                'You pay Twasta, never the builder directly',
                `Twasta pays ${builder} for a stage only after you approve it`,
                `If ${builder} doesn’t accept, the advance is refunded in full`,
                'Disputes are decided using the build history and test results',
              ].map((p) => (
                <div key={p} style={{ display: 'flex', gap: 10, fontSize: 14 }}>
                  <span style={{ color: 'var(--color-success)', flex: '0 0 auto', marginTop: 2 }}><Check size={18} /></span><span>{p}</span>
                </div>
              ))}
            </div>
          </aside>
        </div>
      </div>
    </MarketplaceShell>
  )
}
